import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Card } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import AppCard from 'shared/design-system/components/app-card';
import NoData from 'shared/design-system/components/no-data-box';
import ServerSidePaginator from 'shared/design-system/components/app-server-side-paginator';
import {
  RptOpportunityDto,
  RptOpportunitySearchDto,
  RptOpportunitySearchDtoSortByEnum,
  RptOpportunitySearchDtoSortDirectionEnum,
  RptOpportunitySearchDtoStatiEnum,
} from '@api/client';
import { searchOpportunities, saveFiltersOpportunitiesUsed } from './_redux/actions';
import { opportunitiesSelector, opportunitiesPagingSelector, filtersOpportunitiesUsedSelector } from './_redux/selectors';
import { getAgendaFilterOptions } from '../agenda-actions/_redux/actions';
import { agendaFilterOptionsSelector } from '../agenda-actions/_redux/selectors';
import { getCrudFilters } from 'pages/private/crud/_redux/actions';
import { filtriCrudSelector } from 'pages/private/crud/_redux/selectors';
import { getProjectSearchFilter } from 'pages/private/project-list/_redux/actions';
import { filtriProgettiSelector } from 'pages/private/project-list/_redux/selectors';
import AuthService from 'core/auth/auth.service';
import OpportunityFilters from './_components/opportunity-filters';
import OpportunityTable from './_components/opportunity-table';
import AddAgendaActionModal from '../agenda-actions/_components/add-agenda-action-modal';
import OpportunityActivitiesModal from './_components/opportunity-activities-modal';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT: Pick<RptOpportunitySearchDto, 'sortBy' | 'sortDirection'> = {
  sortBy: RptOpportunitySearchDtoSortByEnum.OppNumAttivitaInAgenda,
  sortDirection: RptOpportunitySearchDtoSortDirectionEnum.Desc,
};

const AgendaOpportunities: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const opportunities = useAppSelector(opportunitiesSelector);
  const paging = useAppSelector(opportunitiesPagingSelector);
  const filtersUsed = useAppSelector(filtersOpportunitiesUsedSelector);
  const agendaFilterOptions = useAppSelector(agendaFilterOptionsSelector);
  const filtriCrud = useAppSelector(filtriCrudSelector);
  const filtriProgetti = useAppSelector(filtriProgettiSelector);
  const isAdmin = AuthService.isSuperAdmin();

  // Stati di default: tutti gli stati disponibili tranne CLOSED_LOST
  const defaultStati = useMemo<RptOpportunitySearchDtoStatiEnum[] | undefined>(() => {
    const all = agendaFilterOptions?.statiOpportunity;
    if (!all?.length) return undefined;
    return all.filter((s) => s !== RptOpportunitySearchDtoStatiEnum.ClosedLost);
  }, [agendaFilterOptions?.statiOpportunity]);

  const defaultFilters = useMemo<Pick<RptOpportunitySearchDto, 'stati' | 'lastModified'>>(() => ({
    ...(defaultStati?.length ? { stati: defaultStati } : {}),
  }), [defaultStati]);

  const initialSearchFired = useRef(false);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<RptOpportunityDto | undefined>(undefined);
  const [selectedClienteName, setSelectedClienteName] = useState<string | undefined>(undefined);
  const [selectedStrutturaName, setSelectedStrutturaName] = useState<string | undefined>(undefined);

  const handleCreateActivity = (opp: RptOpportunityDto) => {
    const clienteNome = agendaFilterOptions?.accounts?.find((a) => a.idAccount === opp.fkCliente)?.nome
      ?? opp.fkCliente
      ?? undefined;
    const buList = isAdmin ? (filtriCrud?.businessUnit || []) : (filtriProgetti?.businessUnit || []);
    const strutturaNome = buList.find((bu) => bu.id === opp.fkStrutturaBusiness)?.name
      ?? (opp.fkStrutturaBusiness != null ? String(opp.fkStrutturaBusiness) : undefined);
    setSelectedOpportunity(opp);
    setSelectedClienteName(clienteNome);
    setSelectedStrutturaName(strutturaNome);
    setShowCreateModal(true);
  };

  const handleViewActivities = (opp: RptOpportunityDto) => {
    setSelectedOpportunity(opp);
    setShowActivitiesModal(true);
  };

  useEffect(() => {
    if (!agendaFilterOptions?.accounts?.length) dispatch(getAgendaFilterOptions());
    if (isAdmin && !filtriCrud?.businessUnit?.length) dispatch(getCrudFilters());
    if (!isAdmin && !filtriProgetti?.businessUnit?.length) dispatch(getProjectSearchFilter());
  }, []);

  useEffect(() => {
    if (initialSearchFired.current) return;
    // Aspetta che siano caricati gli stati per derivare il default (tutti tranne CLOSED_LOST)
    if (!defaultStati) return;
    initialSearchFired.current = true;
    const dto: RptOpportunitySearchDto = {
      ...DEFAULT_SORT,
      ...defaultFilters,
      paging: { page: 0, size: DEFAULT_PAGE_SIZE },
    };
    dispatch(saveFiltersOpportunitiesUsed(dto));
    dispatch(searchOpportunities({ rptOpportunitySearchDto: dto }));
  }, [defaultStati, defaultFilters]);

  const handleSearch = (filters: Partial<RptOpportunitySearchDto>) => {
    const dto: RptOpportunitySearchDto = {
      ...DEFAULT_SORT,
      ...filters,
      paging: { page: 0, size: DEFAULT_PAGE_SIZE },
    };
    dispatch(saveFiltersOpportunitiesUsed(dto));
    dispatch(searchOpportunities({ rptOpportunitySearchDto: dto }));
  };

  const handleReset = () => {
    const dto: RptOpportunitySearchDto = {
      ...DEFAULT_SORT,
      ...defaultFilters,
      paging: { page: 0, size: DEFAULT_PAGE_SIZE },
    };
    dispatch(saveFiltersOpportunitiesUsed(dto));
    dispatch(searchOpportunities({ rptOpportunitySearchDto: dto }));
  };

  const changePage = (page: number) => {
    const dto: RptOpportunitySearchDto = {
      ...filtersUsed,
      paging: { ...filtersUsed?.paging, page, size: DEFAULT_PAGE_SIZE },
    };
    dispatch(saveFiltersOpportunitiesUsed(dto));
    dispatch(searchOpportunities({ rptOpportunitySearchDto: dto }));
  };

  const refreshOpportunities = () => {
    const dto: RptOpportunitySearchDto = filtersUsed
      ? { ...filtersUsed, paging: { ...filtersUsed.paging, page: 0, size: DEFAULT_PAGE_SIZE } }
      : { ...DEFAULT_SORT, paging: { page: 0, size: DEFAULT_PAGE_SIZE } };
    dispatch(saveFiltersOpportunitiesUsed(dto));
    dispatch(searchOpportunities({ rptOpportunitySearchDto: dto }));
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <h4 className="text-gray-800 my-0">{t('Opportunity')}</h4>
      </div>

      <Accordion className="acc-btn my-3">
        <Card className="border-left-primary acc-btn">
          <Card.Header
            className={`cursor-pointer d-flex align-items-center card-header-border ${filtersOpen ? 'card-header-border' : ''}`}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <b className="text-blue-links my-auto ms-2 me-3">{t('cdg.filters')}</b>
            <div className="ms-auto">
              <i className={`fa-solid fa-chevron-${filtersOpen ? 'up' : 'down'} notification-header`} />
            </div>
          </Card.Header>
          {filtersOpen && (
            <Card.Body className="mx-2">
              <OpportunityFilters
                onSearch={handleSearch}
                onReset={handleReset}
                initialValues={defaultFilters}
              />
            </Card.Body>
          )}
        </Card>
      </Accordion>

      <AppCard title={t('Elenco opportunity')} className="mt-3">
        {opportunities.length === 0 ? (
          <NoData msg={t('Nessuna opportunity trovata')} />
        ) : (
          <OpportunityTable
            data={opportunities}
            onCreateActivity={handleCreateActivity}
            onViewActivities={handleViewActivities}
          />
        )}
        {opportunities.length > 0 && (
          <ServerSidePaginator
            currentPage={paging?.page || 0}
            totalPages={paging?.totalPages || 0}
            elementsToShow={opportunities.length}
            totalElements={paging?.totalElements}
            onPageChange={(e) => changePage(e)}
          />
        )}
      </AppCard>
      <AddAgendaActionModal
        show={showCreateModal}
        setShow={setShowCreateModal}
        prefilledOpportunity={selectedOpportunity}
        prefilledClienteName={selectedClienteName}
        prefilledStrutturaName={selectedStrutturaName}
        onSaved={refreshOpportunities}
      />
      <OpportunityActivitiesModal
        show={showActivitiesModal}
        setShow={setShowActivitiesModal}
        opportunity={selectedOpportunity}
      />
    </>
  );
};

export default AgendaOpportunities;
